import React from 'react';
import { Form, Button, message, Typography, App, Input } from 'antd';
import { ExclamationCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { FormikValues, FieldArray, FormikProps } from 'formik';
import { shallowEqual, useSelector } from 'react-redux';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import DrawerFormik from '../../global/drawer/DrawerFormik';
import { ApplicationState } from '../../../reducers';
import FormInput from '../../global/Forms/FormInput';
import FormSelect from '../../global/Forms/FormSelect';
import { updateCustomField, deleteCustomField } from '../../../actions/brand/custom_fields';
import { AsyncDispatch } from '../../../../types/global';
import {
  BrandCustomField,
  CustomFieldType,
  CustomFieldOption,
  CustomFieldGroup,
} from '../../../../types/custom_fields';
import SettingsCustomFieldOptions from './SettingsCustomFieldOptions';
import globalTranslations from '../../../constants/GlobalTranslation.json';
import { intercomEvent } from '../../../utils/IntercomUtils';
import { getSelectedBrandCode } from '../../../selectors/brand/brandSelector';
import FormSwitch from '../../global/Forms/FormSwitch';
import AntTooltip from '../../global/AntTooltip';
import { useAppDispatch } from '../../../store/ReduxStore';
import { isManufacturer } from '../../../utils/UserUtils';
import {
  getTableDelimiterValidation,
  getTableRowDelimiterValidation,
  getProductRelationDelimiterValidation,
  getProductRelationBrandCodeDelimiterValidation,
} from '../../../utils/CustomFieldValidationSchemas';

type SettingsCustomFieldEditDrawerProps = {
  visible: boolean;
  canEditSettingsCustomFields: boolean;
  closeDrawer: () => void;
  handleAddField: (id: number, groupId: number | null) => void;
  handleDeleteField: (id: number, groupId: number | null) => void;
  customFieldsLength: number;
  customField?: BrandCustomField;
  customFields: BrandCustomField[];
  groups: CustomFieldGroup[];
};

const SettingsCustomFieldEditDrawer: React.FC<SettingsCustomFieldEditDrawerProps> = ({
  visible,
  canEditSettingsCustomFields,
  closeDrawer,
  handleAddField,
  handleDeleteField,
  customFieldsLength,
  customField,
  customFields,
  groups,
}) => {
  const { t } = useTranslation();
  const dispatch: AsyncDispatch = useAppDispatch();
  const { modal } = App.useApp();

  const {
    customFieldTypes,
    brandId,
    brandCode,
    fetchingScripts,
    scripts,
    user,
    enhancedValidations,
  } = useSelector((state: ApplicationState) => {
    return {
      customFieldTypes: state.resources.data.custom_field?.types || [],
      brandId: state.parent.brands.selectedBrandId,
      brandCode: getSelectedBrandCode(state),
      fetchingScripts: state.distribution.apiSettings.fetchingScripts,
      scripts: state.distribution.apiSettings.scripts,
      user: state.user.user,
      enhancedValidations: state.brand.customFields.enhancedValidations || [],
    };
  }, shallowEqual);

  const manufacturer = React.useMemo(() => isManufacturer(user!), [user]);
  const pdmUser = React.useMemo(() => user?.email.endsWith('pdm.ai'), [user]);

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .required(t('validation:required'))
      .test(
        'check-unique-name',
        t('settings:customFields.validation.customFieldIsUsed'),
        (val: any) => {
          return !customField || (customField && customField.name !== val)
            ? customFields.every(({ name }) => name !== val)
            : true;
        }
      ),
    customFieldTypeId: Yup.number().nullable().required(t('validation:required')),
    options: Yup.array().when(['customFieldTypeId'], ([customFieldTypeId], schema) =>
      customFieldTypeId === CustomFieldType.Dropdown
        ? Yup.array()
            .of(Yup.object().shape({ name: Yup.string().required() }))
            .test(
              'check-unique-option-name',
              t('settings:customFields.validation.dontUseDuplicateOptions'),
              array => array!.length === new Set(array!.map((a: any) => a.name)).size
            )
            .min(1, t('settings:customFields.validation.atLeastOneOptionIsRequired'))
        : schema
    ),
    headers: Yup.array().when(['customFieldTypeId'], ([customFieldTypeId], schema) =>
      customFieldTypeId === CustomFieldType.Table
        ? Yup.array()
            .of(Yup.object().shape({ name: Yup.string().required() }))
            .test(
              'check-unique-header-name',
              t('settings:customFields.validation.dontUseDuplicateOptions'),
              array => array!.length === new Set(array!.map((a: any) => a.name)).size
            )
            .min(1, t('settings:customFields.validation.atLeastOneOptionIsRequired'))
        : schema
    ),
    tableDelimiter: Yup.string().when(['customFieldTypeId'], ([customFieldTypeId], schema) =>
      customFieldTypeId === CustomFieldType.Table ? getTableDelimiterValidation(t) : schema
    ),
    tableRowDelimiter: Yup.string().when(['customFieldTypeId'], ([customFieldTypeId], schema) =>
      customFieldTypeId === CustomFieldType.Table ? getTableRowDelimiterValidation(t) : schema
    ),
    productRelationDelimiter: Yup.string().when(
      ['customFieldTypeId'],
      ([customFieldTypeId], schema) =>
        customFieldTypeId === CustomFieldType.Product_Relation
          ? getProductRelationDelimiterValidation(t)
          : schema
    ),
    productRelationBrandCodeDelimiter: Yup.string().when(
      ['customFieldTypeId'],
      ([customFieldTypeId], schema) =>
        customFieldTypeId === CustomFieldType.Product_Relation
          ? getProductRelationBrandCodeDelimiterValidation(t, true)
          : schema
    ),
  });

  const drawerProps = {
    visible,
    width: 450,
    title: t('glossary:segments.customField_one'),
    validationSchema,
    destroyOnClose: true,
    initialValues: {
      name: customField ? customField.name : '',
      description: customField?.description || '',
      customFieldTypeId: customField ? customField.custom_field_type_id : CustomFieldType.Input,
      options:
        customField && customField.options
          ? customField.options.sort((a, b) => a.record_number - b.record_number)
          : [],
      headers:
        customField && customField.headers
          ? customField.headers.sort((a, b) => a.record_number - b.record_number)
          : [],
      tableDelimiter: customField?.table_delimiter || '|',
      tableRowDelimiter: customField?.table_row_delimiter || '~n~',
      productRelationDelimiter: customField?.product_relation_delimiter || ',',
      productRelationBrandCodeDelimiter: customField?.product_relation_brand_code_delimiter || '',
      publishChanges: customField ? !customField.ignore_on_sync : true,
      scriptId: customField && customField.script_id ? customField.script_id : null,
      groupId: customField ? customField.group_id : null,
      enhancedValidationIds:
        customField && customField.enhanced_validation_ids
          ? customField.enhanced_validation_ids.split(',').map(id => Number(id.trim()))
          : [],
    },
    deleteButtonEnabled: canEditSettingsCustomFields,
    additionalFooterData:
      customField?.dynamic_configuration && !pdmUser
        ? t('settings:customFields:dynamicFieldDeleteInfo')
        : undefined,
    handleSaveButtonEnabled: (formik: FormikProps<FormikValues>) =>
      canEditSettingsCustomFields && formik.dirty,
    onSubmit: (values: FormikValues) => {
      const enhancedValidationIdsString =
        values.enhancedValidationIds && values.enhancedValidationIds.length > 0
          ? values.enhancedValidationIds.join(',')
          : null;

      dispatch(
        updateCustomField({
          customFieldId: customField ? customField.id : null,
          name: values.name,
          description: values.description || null,
          brandId,
          customFieldTypeId: values.customFieldTypeId,
          recordNumber: customField ? customField.record_number : customFieldsLength,
          options: values.customFieldTypeId === CustomFieldType.Dropdown ? values.options : null,
          headers: values.customFieldTypeId === CustomFieldType.Table ? values.headers : null,
          tableDelimiter:
            values.customFieldTypeId === CustomFieldType.Table ? values.tableDelimiter : null,
          tableRowDelimiter:
            values.customFieldTypeId === CustomFieldType.Table ? values.tableRowDelimiter : null,
          productRelationDelimiter:
            values.customFieldTypeId === CustomFieldType.Product_Relation
              ? values.productRelationDelimiter
              : null,
          productRelationBrandCodeDelimiter:
            values.customFieldTypeId === CustomFieldType.Product_Relation
              ? values.productRelationBrandCodeDelimiter
              : null,
          ignoreOnSync: values.publishChanges ? 0 : 1,
          scriptId: values.customFieldTypeId === CustomFieldType.Button ? values.scriptId : null,
          groupId: values.groupId === 'other' ? null : values.groupId,
          enhancedValidationIds: enhancedValidationIdsString,
        })
      ).then(result => {
        const newFieldId = result.value.data.custom_field_id;
        handleAddField(newFieldId, values.groupId);
        closeDrawer();
      });
    },
    onDelete:
      customField && (!customField.dynamic_configuration || pdmUser)
        ? () => {
            intercomEvent('viewed-brand-settings', {
              location: 'settings-custom-fields',
              brand_code: brandCode!,
              action: 'delete',
            });

            modal.confirm({
              title: t('settings:customFields.confirmDelete'),
              icon: <ExclamationCircleOutlined />,
              okText: t('common:delete'),
              okButtonProps: { danger: true },
              onOk() {
                dispatch(deleteCustomField(Number(customField.id)))
                  .then(() => {
                    handleDeleteField(Number(customField.id), customField.group_id);
                    closeDrawer();
                  })
                  .catch(e => {
                    const error = e.response.data.message || globalTranslations.unknownError;
                    message.error(error);
                  });
              },
            });
          }
        : undefined,
    onClose: closeDrawer,
  };

  return (
    <DrawerFormik {...drawerProps}>
      {({ values, handleSubmit, setFieldValue, errors, submitCount }) => (
        <Form layout="vertical" onFinish={() => handleSubmit()}>
          {customField?.dynamic_configuration && (
            <h3 className="mb-7">{t('settings:customFields.dynamicCustomField')}</h3>
          )}
          <FormInput
            name="name"
            label={t('common:name')}
            disabled={!!customField?.dynamic_configuration && !pdmUser}
            required
          />
          <Form.Item label={t('common:description')}>
            <Input.TextArea
              value={values.description || ''}
              onChange={e => setFieldValue('description', e.target.value)}
              rows={3}
              placeholder={t('common:description')}
              disabled={!!customField?.dynamic_configuration && !pdmUser}
            />
          </Form.Item>
          <FormSelect
            name="customFieldTypeId"
            label={t('common:type')}
            values={customFieldTypes}
            disabled={!!customField?.dynamic_configuration && !pdmUser}
            required
          />
          {values.customFieldTypeId === CustomFieldType.Dropdown && (
            <FieldArray
              name="options"
              render={arrayHelpers => (
                <div className="flex-col">
                  <Button
                    type="primary"
                    ghost
                    onClick={() => {
                      const maxRecordNumber =
                        values.options.length > 0
                          ? Math.max(
                              ...values.options.map((opt: CustomFieldOption) => opt.record_number)
                            )
                          : 0;
                      arrayHelpers.push({
                        name: '',
                        record_number: maxRecordNumber + 1,
                      });
                    }}
                    disabled={!!customField?.dynamic_configuration && !pdmUser}
                  >
                    {t('settings:customFields.addNewOption')}
                  </Button>
                  {values.options.length > 0 && (
                    <SettingsCustomFieldOptions
                      options={values.options}
                      disabled={!!customField?.dynamic_configuration && !pdmUser}
                      updateOptions={(options: CustomFieldOption[]) => {
                        setFieldValue('options', options);
                      }}
                    />
                  )}
                </div>
              )}
            />
          )}
          {values.customFieldTypeId === CustomFieldType.Button && (
            <FormSelect
              name="scriptId"
              label={t('api:script')}
              values={scripts}
              loading={fetchingScripts}
              disabled={!!customField?.dynamic_configuration && !pdmUser}
              required
            />
          )}
          {values.customFieldTypeId === CustomFieldType.Table && (
            <>
              <FieldArray
                name="headers"
                render={arrayHelpers => (
                  <div className="flex-col">
                    <Button
                      type="primary"
                      ghost
                      onClick={() => {
                        const maxRecordNumber =
                          values.headers.length > 0
                            ? Math.max(
                                ...values.headers.map(
                                  (header: CustomFieldOption) => header.record_number
                                )
                              )
                            : 0;
                        arrayHelpers.push({
                          name: '',
                          record_number: maxRecordNumber + 1,
                        });
                      }}
                      disabled={!!customField?.dynamic_configuration && !pdmUser}
                    >
                      Add Column Header
                    </Button>
                    {values.headers.length > 0 && (
                      <SettingsCustomFieldOptions
                        options={values.headers}
                        disabled={!!customField?.dynamic_configuration && !pdmUser}
                        updateOptions={(headers: CustomFieldOption[]) => {
                          setFieldValue('headers', headers);
                        }}
                      />
                    )}
                  </div>
                )}
              />
              <FormInput
                name="tableDelimiter"
                label="Table Delimiter"
                disabled={!!customField?.dynamic_configuration && !pdmUser}
                required
                className="mt-5"
              />
              <FormInput
                name="tableRowDelimiter"
                label="Table Row Delimiter"
                disabled={!!customField?.dynamic_configuration && !pdmUser}
                required
              />
            </>
          )}
          {values.customFieldTypeId === CustomFieldType.Product_Relation && (
            <>
              <FormInput
                name="productRelationDelimiter"
                label={t('settings:customFields.productRelationDelimiter')}
                disabled={!!customField?.dynamic_configuration && !pdmUser}
                required
                className="mt-5"
              />
              <FormInput
                name="productRelationBrandCodeDelimiter"
                label={
                  <>
                    {t('settings:customFields.productRelationBrandCodeDelimiter')}
                    {!customField && (
                      <AntTooltip
                        title={t(
                          'settings:customFields.productRelationBrandCodeDelimiterTooltipSimple'
                        )}
                      >
                        <InfoCircleOutlined className="ml-1 icon__blue" />
                      </AntTooltip>
                    )}
                  </>
                }
                disabled={!!customField}
                required
              />
            </>
          )}
          <FormSelect
            name="groupId"
            label={t('common:group')}
            values={groups.map(g => ({
              ...g,
              id: g.id === 'other' || typeof g.id === 'number' ? g.id : Number(g.id.substring(1)),
            }))}
            required
          />

          <FormSelect
            name="enhancedValidationIds"
            label={t('settings:customFields.enhancedValidations')}
            values={enhancedValidations}
            mode="multiple"
            placeholder={t('settings:customFields.selectEnhancedValidations')}
            disabled={!!customField?.dynamic_configuration && !pdmUser}
          />

          <Typography.Title level={5} className="mt-5">
            {t('settings:customFields.customfieldSettings')}
          </Typography.Title>
          <div className="flex">
            <FormSwitch
              name="publishChanges"
              checkedChildren={t('common:yes')}
              unCheckedChildren={t('common:no')}
            />
            <span className="h-8 ml-3 leading-8">
              {manufacturer
                ? t('settings:customFields.publishManufacturerChanges')
                : t('settings:customFields.updateTimeStamp')}
              <AntTooltip
                title={
                  manufacturer
                    ? t('settings:customFields.publishChangesInfo')
                    : t('settings:customFields.publishReceiverInfo')
                }
              >
                <InfoCircleOutlined className="icon__blue ml-1" />
              </AntTooltip>
            </span>
          </div>
          {submitCount > 0 && errors.options && (
            <div className="has-error">
              <span className="ant-form-explain ">
                {Array.isArray(errors.options)
                  ? t('settings:customFields.validation.fillInAllInputFields')
                  : (errors.options as string)}
              </span>
            </div>
          )}
          {submitCount > 0 && errors.headers && (
            <div className="has-error">
              <span className="ant-form-explain ">
                {Array.isArray(errors.headers)
                  ? t('settings:customFields.validation.fillInAllInputFields')
                  : (errors.headers as string)}
              </span>
            </div>
          )}
        </Form>
      )}
    </DrawerFormik>
  );
};

export default SettingsCustomFieldEditDrawer;
